import React from 'react';
import { motion } from 'framer-motion';
import { FiArrowLeft } from 'react-icons/fi';
import { Link } from 'react-router-dom';

const TermsOfService = () => {
  // Scroll to top on component mount
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen">
      <div className="container mx-auto px-4 py-20">
        <div className="max-w-4xl mx-auto">
          <Link to="/" className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-8">
            <FiArrowLeft className="mr-2" />
            Back to Home
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="glass-card p-8 md:p-12 rounded-2xl"
          >
            <h1 className="text-3xl md:text-4xl font-display font-bold mb-8">Terms of Service</h1>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-lg">Last Updated: June 1, 2025</p>
              
              <h2>1. Agreement to Terms</h2>
              <p>
                These Terms of Service ("Terms") constitute a legally binding agreement between you and xdimension AI Corp. 
                ("xdimension," "we," "our," or "us") regarding your access to and use of the xdimension.ai website 
                and any related services, products, or content (collectively, the "Services").
              </p>
              <p>
                By accessing or using our Services, you agree to be bound by these Terms and our Privacy Policy. 
                If you do not agree to these Terms, please do not access or use our Services.
              </p>

              <h2>2. Changes to Terms</h2>
              <p>
                We may modify these Terms at any time. If we make changes, we will provide notice of such changes, such as by sending an 
                email notification, providing notice through our Services, or updating the "Last Updated" date at the beginning of 
                these Terms. Your continued use of our Services following the notice of updated Terms constitutes your acceptance 
                and agreement to the changes. If you do not agree to the revised Terms, you may not access or use our Services.
              </p>

              <h2>3. Eligibility</h2>
              <p>
                You must be at least 16 years old to access or use our Services. By accessing or using our Services, you represent 
                and warrant that you are at least 16 years old and that you have the right, authority, and capacity to enter into 
                these Terms and to abide by all of the terms and conditions set forth herein.
              </p>

              <h2>4. Account Responsibilities</h2>
              <p>
                If you create an account with us, you are responsible for maintaining the confidentiality of your account and password, 
                and for restricting access to your computer or device. You agree to accept responsibility for all activities that occur 
                under your account or password.
              </p>
              <p>
                We reserve the right to refuse service, terminate accounts, remove or edit content, or cancel orders at our sole discretion.
              </p>

              <h2>5. Intellectual Property Rights</h2>
              <p>
                All content included on this site, such as text, graphics, logos, button icons, images, audio clips, digital downloads, 
                data compilations, and software, is the property of xdimension or its content suppliers and protected by international 
                copyright laws.
              </p>
              <p>
                The compilation of all content on this site is the exclusive property of xdimension and protected by international 
                copyright laws. All software used on this site is the property of xdimension or its software suppliers and protected by 
                international copyright laws.
              </p>

              <h2>6. User Conduct</h2>
              <p>You agree not to:</p>
              <ul>
                <li>
                  Use our Services for any illegal purpose or in violation of any local, state, national, or international law
                </li>
                <li>
                  Violate or encourage others to violate the rights of third parties, including intellectual property rights
                </li>
                <li>
                  Post, upload, or distribute any content that is unlawful, defamatory, libelous, inaccurate, or that a reasonable 
                  person could deem to be objectionable, profane, indecent, pornographic, harassing, threatening, embarrassing, 
                  hateful, or otherwise inappropriate
                </li>
                <li>
                  Interfere with security-related features of our Services
                </li>
                <li>
                  Interfere with the operation of our Services or any user's enjoyment of the Services
                </li>
                <li>
                  Use any robot, spider, crawler, scraper, or other automated means to access our Services
                </li>
                <li>
                  Attempt to circumvent any technological measure implemented by us or any of our providers or any other third party 
                  to protect our Services
                </li>
                <li>
                  Use our Services in any manner that could interfere with, disrupt, negatively affect, or inhibit other users from 
                  fully enjoying our Services
                </li>
              </ul>

              <h2>7. Product and Service Descriptions</h2>
              <p>
                We endeavor to describe our products and services as accurately as possible. However, we do not warrant that product 
                descriptions or other content of our Services is accurate, complete, reliable, current, or error-free.
              </p>
              <p>
                The Ainous AI assistant and related products described on our website are in development. Features, specifications, and 
                release dates are subject to change.
              </p>

              <h2>8. Pre-orders</h2>
              <p>
                Pre-orders are an expression of interest in purchasing future products. By submitting a pre-order, you are not obligated 
                to complete the purchase, and we are not obligated to produce or deliver the product. We will notify you when the product 
                is available, at which point you will have the option to complete your purchase.
              </p>
              <p>
                Pre-order funds may be used for product development, manufacturing, and business operations.
              </p>

              <h2>9. Limitation of Liability</h2>
              <p>
                To the maximum extent permitted by law, in no event shall xdimension, its directors, employees, partners, agents, 
                suppliers, or affiliates, be liable for any indirect, incidental, special, consequential, or punitive damages, including 
                without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from:
              </p>
              <ul>
                <li>Your access to or use of or inability to access or use our Services</li>
                <li>Any conduct or content of any third party on our Services</li>
                <li>Any content obtained from our Services</li>
                <li>Unauthorized access, use, or alteration of your transmissions or content</li>
              </ul>

              <h2>10. Disclaimer of Warranties</h2>
              <p>
                Our Services are provided on an "as is" and "as available" basis. xdimension and its suppliers and licensors hereby 
                disclaim all warranties of any kind, express or implied, including, without limitation, the warranties of merchantability, 
                fitness for a particular purpose, and non-infringement.
              </p>
              <p>
                Neither xdimension nor its suppliers and licensors makes any warranty that our Services will be error-free or that access 
                thereto will be continuous or uninterrupted.
              </p>

              <h2>11. Governing Law</h2>
              <p>
                These Terms shall be governed and construed in accordance with the laws of California, United States, without regard to 
                its conflict of law provisions.
              </p>
              <p>
                Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights. If any 
                provision of these Terms is held to be invalid or unenforceable by a court, the remaining provisions of these Terms 
                will remain in effect.
              </p>

              <h2>12. Dispute Resolution</h2>
              <p>
                Any dispute arising from or relating to the subject matter of these Terms shall be finally settled by arbitration in 
                San Francisco, California, using the English language in accordance with the Arbitration Rules and Procedures of 
                Judicial Arbitration and Mediation Services, Inc. ("JAMS") then in effect, by one commercial arbitrator with substantial 
                experience in resolving intellectual property and commercial contract disputes.
              </p>

              <h2>13. Entire Agreement</h2>
              <p>
                These Terms constitute the entire agreement between you and xdimension regarding our Services, and supersede and replace 
                any prior agreements we might have had between us regarding the Services.
              </p>

              <h2>14. Contact Information</h2>
              <p>
                If you have any questions about these Terms, please contact us at:
              </p>
              <p>
                xdimension AI<br />
                Website: xdimension.ai<br />
                Email: legal@xdimension.ai
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;